// Users × working days (architecture sprint-05 §6.3). States and order come from the server; the client
// only formats. Logged and missing cells link to Records for that user and day.
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/cn';
import { formatDurationHm, formatLocalDate } from '@/lib/format';
import { recordsLinkFor, trackerColumnLabel } from '@/lib/tracker-model';
import type { Tracker, TrackerCellState, TrackerRow } from '@/types/admin';

const MISSING_MARK = '—';

const CELL_CLASS: Record<TrackerCellState, string> = {
  logged: '',
  missing: 'bg-danger/10 text-danger-fg',
  na: 'bg-muted/40',
};

function cellName(row: TrackerRow, date: string, state: TrackerCellState, seconds: number): string {
  const prefix = `${row.user.name}, ${formatLocalDate(date)}`;
  if (state === 'logged') return `${prefix}: ${formatDurationHm(seconds)}`;
  if (state === 'missing') return `${prefix}: missing`;
  return `${prefix}: not applicable`;
}

type LinkFor = (userId: string, date: string) => string;

function Cell({ row, date, index, linkFor }: { row: TrackerRow; date: string; index: number; linkFor: LinkFor }): JSX.Element {
  const state = row.states[index] ?? 'na';
  const seconds = row.seconds[index] ?? 0;
  const label = cellName(row, date, state, seconds);
  const className = cn('px-2 py-1.5 text-center font-mono text-xs tabular-nums', CELL_CLASS[state]);
  if (state === 'na') return <td className={className} aria-label={label} />;
  return (
    <td className={className}>
      <Link to={linkFor(row.user.id, date)} aria-label={label} className="block rounded hover:underline">
        {state === 'logged' ? formatDurationHm(seconds) : MISSING_MARK}
      </Link>
    </td>
  );
}

/** Sprint 11: `linkFor` sends a cell to the right Records page (default: Admin records, all projects). */
export function TrackerGrid({ tracker, linkFor = recordsLinkFor }: { tracker: Tracker; linkFor?: LinkFor }): JSX.Element {
  return (
    <div className="overflow-x-auto rounded-xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-card">
          <tr className="border-b border-subtle">
            <th scope="col" className="sticky left-0 z-10 bg-card px-3 py-2 text-left text-xs font-normal text-muted-foreground">
              Person
            </th>
            {tracker.dates.map((d) => (
              <th key={d} scope="col" className="whitespace-nowrap px-2 py-2 text-center text-xs font-normal text-muted-foreground">
                {trackerColumnLabel(d)}
              </th>
            ))}
            <th scope="col" className="px-2 py-2 text-right text-xs font-normal text-muted-foreground">
              Total
            </th>
            <th scope="col" className="px-2 py-2 text-right text-xs font-normal text-muted-foreground">
              Missing
            </th>
          </tr>
        </thead>
        <tbody>
          {tracker.rows.map((row) => (
            <tr key={row.user.id} className="border-b border-subtle">
              <th
                scope="row"
                className="sticky left-0 z-10 whitespace-nowrap bg-card px-3 py-1.5 text-left font-normal"
                title={`Started ${formatLocalDate(row.user.startDate)}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  {row.user.name}
                  {row.user.role === 'admin' && <Badge variant="primary">Admin</Badge>}
                </span>
              </th>
              {tracker.dates.map((d, i) => (
                <Cell key={d} row={row} date={d} index={i} linkFor={linkFor} />
              ))}
              <td
                className="whitespace-nowrap px-2 py-1.5 text-right font-mono tabular-nums"
                title={row.offDaySeconds > 0 ? `Includes ${formatDurationHm(row.offDaySeconds)} on non-working days` : undefined}
              >
                {formatDurationHm(row.totalSeconds)}
              </td>
              <td className="px-2 py-1.5 text-right font-mono tabular-nums">{row.missingCount}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className="sticky left-0 z-10 bg-card px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
              Missing
            </th>
            {tracker.missingByDate.map((n, i) => (
              <td key={tracker.dates[i] ?? i} className={cn('px-2 py-1.5 text-center font-mono text-xs tabular-nums', n === 0 && 'text-muted-foreground')}>
                {n}
              </td>
            ))}
            <td />
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
